// 관리자 매출관리 화면(개선항목 4-9).
// 완료(수령완료)로 종결된 주문만 매출로 집계한다.
//  - 좌측: 주문내역 리스트(선택된 기간의 완료 주문). 상단에 기간 선택 + '전체' 버튼.
//  - 우측: 큰 월간 캘린더. 일자별 매출액을 표시하고, 일자를 누르면 해당 일 주문이 좌측에 표시된다.
//
// 날짜 기준은 completedAt(완료 종결 시각)이며, 관리자 브라우저(한국)의 로컬 날짜를 KST 로 간주한다.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  ORDER_TYPE_LABEL_KO,
  formatCartLineLabel,
  formatDesiredPickup,
  type SalesOrderDto,
} from "@oo/shared";
import { PageHeader } from "@/shared/ui";
import { getAdminOrder } from "@/features/orders/ordersApi.ts";
import { listSales } from "@/features/sales/salesApi.ts";
import { useUnits } from "@/features/catalog/useUnits.ts";

// 날짜를 로컬 기준 YYYY-MM-DD 로 포맷.
function ymd(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// 원화 표기 (천 단위 구분).
function won(n: number): string {
  return `${n.toLocaleString("ko-KR")}원`;
}

const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

// 좌측 리스트의 조회 대상을 규정하는 필터.
type Filter =
  | { kind: "month"; year: number; month: number } // month: 0-based
  | { kind: "day"; date: string }
  | { kind: "range"; from: string; to: string }
  | { kind: "all" };

// 필터를 API 쿼리 파라미터(from/to)로 변환.
function filterToRange(f: Filter): { from?: string; to?: string } {
  switch (f.kind) {
    case "month": {
      const first = ymd(new Date(f.year, f.month, 1));
      const last = ymd(new Date(f.year, f.month + 1, 0));
      return { from: first, to: last };
    }
    case "day":
      return { from: f.date, to: f.date };
    case "range":
      return { from: f.from, to: f.to };
    case "all":
      return {};
  }
}

// 필터를 화면 제목 문자열로.
function filterTitle(f: Filter): string {
  switch (f.kind) {
    case "month":
      return `${f.year}년 ${f.month + 1}월 주문내역`;
    case "day":
      return `${f.date} 주문내역`;
    case "range":
      return `${f.from} ~ ${f.to} 주문내역`;
    case "all":
      return "전체 주문내역";
  }
}

// 매출관리 화면. 좌측 주문내역 + 우측 월간 캘린더 2분할로 완료 주문 매출을 조회한다.
export default function SalesPage() {
  const today = new Date();
  // 캘린더가 보여주는 연/월(0-based). 좌측 필터와 별개로 움직일 수 있다.
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  // 좌측 리스트 필터. 기본값은 이번 달 전체.
  const [filter, setFilter] = useState<Filter>({
    kind: "month",
    year: today.getFullYear(),
    month: today.getMonth(),
  });
  // 사용자 지정 기간 입력 보관.
  const [rangeFrom, setRangeFrom] = useState(ymd(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [rangeTo, setRangeTo] = useState(ymd(today));
  // 상세 모달 대상 주문 id (null 이면 닫힘).
  const [detailId, setDetailId] = useState<string | null>(null);

  // 캘린더에 표시할 '해당 월' 완료 주문(일자별 매출 집계용).
  const monthFrom = ymd(new Date(calYear, calMonth, 1));
  const monthTo = ymd(new Date(calYear, calMonth + 1, 0));
  const monthQuery = useQuery({
    queryKey: ["admin-sales", "month", monthFrom, monthTo],
    queryFn: () => listSales({ from: monthFrom, to: monthTo }),
  });

  // 좌측 리스트용 완료 주문(선택된 필터 기준).
  const range = filterToRange(filter);
  const listQuery = useQuery({
    queryKey: ["admin-sales", "list", filter],
    queryFn: () => listSales(range),
  });

  // 일자별 매출 집계 (캘린더 셀 표시용). key = YYYY-MM-DD.
  const dailyTotals = useMemo(() => {
    const map = new Map<string, { sales: number; count: number }>();
    for (const o of monthQuery.data ?? []) {
      const key = ymd(new Date(o.completedAt));
      const cur = map.get(key) ?? { sales: 0, count: 0 };
      cur.sales += o.totalKrw;
      cur.count += 1;
      map.set(key, cur);
    }
    return map;
  }, [monthQuery.data]);

  // 캘린더 그리드(앞쪽 빈칸 포함) 구성.
  const calendarCells = useMemo(() => {
    const firstWeekday = new Date(calYear, calMonth, 1).getDay(); // 0=일
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const cells: (string | null)[] = [];
    for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push(ymd(new Date(calYear, calMonth, d)));
    }
    return cells;
  }, [calYear, calMonth]);

  // 캘린더 월 이동. 이동 시 좌측 리스트도 그 달 전체로 맞춘다.
  function moveMonth(delta: number) {
    const base = new Date(calYear, calMonth + delta, 1);
    const y = base.getFullYear();
    const m = base.getMonth();
    setCalYear(y);
    setCalMonth(m);
    setFilter({ kind: "month", year: y, month: m });
  }

  // 선택된 일자(하이라이트용).
  const selectedDay = filter.kind === "day" ? filter.date : null;

  const orders = listQuery.data ?? [];
  const listTotal = useMemo(() => orders.reduce((sum, o) => sum + o.totalKrw, 0), [orders]);
  // 캘린더 월 전체 매출 합계.
  const monthTotal = useMemo(
    () => (monthQuery.data ?? []).reduce((sum, o) => sum + o.totalKrw, 0),
    [monthQuery.data],
  );

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6 sm:px-6 sm:py-8">
      <PageHeader
        title="매출 관리"
        description="완료된 주문의 매출을 한눈에 확인합니다. 일자를 누르거나 기간을 선택해 조회하세요."
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 좌측: 주문내역 */}
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-ink-200 bg-white p-4">
          {/* 기간 선택 + 전체 */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 text-xs text-ink-600">
                시작
                <input
                  type="date"
                  value={rangeFrom}
                  onChange={(e) => setRangeFrom(e.target.value)}
                  className="rounded-md border border-ink-300 px-2 py-1 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-ink-600">
                종료
                <input
                  type="date"
                  value={rangeTo}
                  onChange={(e) => setRangeTo(e.target.value)}
                  className="rounded-md border border-ink-300 px-2 py-1 text-sm"
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  // 시작/종료가 뒤바뀌어 들어오면 정렬해서 조회한다.
                  const from = rangeFrom <= rangeTo ? rangeFrom : rangeTo;
                  const to = rangeFrom <= rangeTo ? rangeTo : rangeFrom;
                  setFilter({ kind: "range", from, to });
                }}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
              >
                조회
              </button>
              <button
                type="button"
                onClick={() => setFilter({ kind: "all" })}
                className={`rounded-md border px-3 py-1.5 text-sm font-semibold ${
                  filter.kind === "all"
                    ? "border-brand-600 bg-brand-50 text-brand-700"
                    : "border-ink-300 text-ink-700 hover:bg-ink-100"
                }`}
              >
                전체
              </button>
            </div>
          </div>

          {/* 현재 조회 제목 + 합계 */}
          <div className="flex items-baseline justify-between gap-2 border-t border-ink-200 pt-3">
            <h2 className="text-sm font-bold">{filterTitle(filter)}</h2>
            <p className="text-sm">
              <span className="text-ink-600">합계 </span>
              <span className="font-bold text-brand-700 tabular-nums">{won(listTotal)}</span>
              <span className="ml-1 text-xs text-ink-600">({orders.length}건)</span>
            </p>
          </div>

          {/* 주문 리스트 */}
          {listQuery.isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded-md bg-ink-100" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <p className="rounded-md border border-dashed border-ink-300 p-8 text-center text-sm text-ink-600">
              해당 기간에 완료된 주문이 없습니다.
            </p>
          ) : (
            <ul className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto">
              {orders.map((o) => (
                <OrderRow key={o.id} o={o} onClick={() => setDetailId(o.id)} />
              ))}
            </ul>
          )}
        </section>

        {/* 우측: 캘린더 */}
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-ink-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => moveMonth(-1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100"
              aria-label="이전 달"
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>
            <div className="text-center">
              <p className="text-base font-bold">
                {calYear}년 {calMonth + 1}월
              </p>
              <p className="text-xs text-ink-600">
                월 매출 <span className="font-semibold text-brand-700 tabular-nums">{won(monthTotal)}</span>
              </p>
            </div>
            <button
              type="button"
              onClick={() => moveMonth(1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-ink-300 text-ink-700 hover:bg-ink-100"
              aria-label="다음 달"
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>

          {/* 요일 헤더 */}
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-ink-600">
            {WEEKDAY_LABELS.map((w, i) => (
              <div key={w} className={i === 0 ? "text-danger-600" : i === 6 ? "text-brand-700" : ""}>
                {w}
              </div>
            ))}
          </div>

          {/* 날짜 셀 */}
          <div className="grid grid-cols-7 gap-1">
            {calendarCells.map((date, idx) => {
              if (!date) {
                return <div key={`empty-${idx}`} className="aspect-square" />;
              }
              const total = dailyTotals.get(date);
              const dayNum = Number(date.slice(8, 10));
              const isSelected = date === selectedDay;
              const isToday = date === ymd(today);
              return (
                <button
                  key={date}
                  type="button"
                  onClick={() => setFilter({ kind: "day", date })}
                  className={`flex aspect-square flex-col items-start justify-start rounded-md border p-1 text-left transition-colors ${
                    isSelected
                      ? "border-brand-600 bg-brand-50"
                      : "border-ink-200 hover:bg-ink-50"
                  }`}
                >
                  <span
                    className={`text-xs font-semibold ${
                      isToday ? "rounded bg-brand-600 px-1 text-white" : "text-ink-700"
                    }`}
                  >
                    {dayNum}
                  </span>
                  {total ? (
                    <span className="mt-auto w-full truncate text-right text-[10px] font-bold leading-tight text-brand-700 tabular-nums">
                      {total.sales.toLocaleString("ko-KR")}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-ink-600">
            셀 하단 숫자는 해당 일자의 완료 매출액(원)입니다. 일자를 누르면 좌측에 해당 일 주문이 표시됩니다.
          </p>
        </section>
      </div>

      {/* 주문 상세 모달: 좌측 리스트 항목 클릭 시 전체 주문 내역을 보여준다. */}
      {detailId ? (
        <SalesOrderDetailModal orderId={detailId} onClose={() => setDetailId(null)} />
      ) : null}
    </div>
  );
}

// 주문내역 한 줄. 클릭하면 상세 모달을 연다.
function OrderRow({ o, onClick }: { o: SalesOrderDto; onClick: () => void }) {
  // 완료 시각을 'MM-DD HH:mm' 로 간단 표기.
  const d = new Date(o.completedAt);
  const pad = (n: number) => n.toString().padStart(2, "0");
  const when = `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-ink-200 p-3 text-left hover:bg-ink-50"
      >
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <span className="truncate">{o.orderNo}</span>
            <span className="shrink-0 rounded bg-ink-100 px-1 text-[10px] text-ink-600">
              {ORDER_TYPE_LABEL_KO[o.type]}
            </span>
          </p>
          <p className="truncate text-xs text-ink-600">
            {o.customerName ?? "이름 미등록"} · {o.itemCount}개 품목 · {when}
          </p>
        </div>
        <p className="shrink-0 text-sm font-bold tabular-nums">{won(o.totalKrw)}</p>
      </button>
    </li>
  );
}

// 휴대폰 번호 표시 포맷 (하이픈 삽입).
function formatPhone(p: string): string {
  if (p.length === 11) return `${p.slice(0, 3)}-${p.slice(3, 7)}-${p.slice(7)}`;
  if (p.length === 10) return `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}`;
  return p;
}

// 주문 상세 모달. 클릭한 주문의 전체 내역(주문자/연락처/수령/배송지/품목/금액)을 보여준다.
// 매출관리는 완료 주문만 다루므로 상태 변경 버튼 없이 조회 전용으로 둔다.
function SalesOrderDetailModal({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const units = useUnits();
  const query = useQuery({
    queryKey: ["admin-order", orderId],
    queryFn: () => getAdminOrder(orderId),
  });
  const order = query.data;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="주문 상세"
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-y-auto rounded-lg bg-surface p-5 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-bold text-ink-900">
            주문 상세
            {order ? <span className="ml-2 text-sm font-normal text-ink-600">{order.orderNo}</span> : null}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-600 hover:bg-ink-100"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {query.isLoading ? (
          <div className="mt-4 h-40 animate-pulse rounded-md bg-ink-100" />
        ) : !order ? (
          <p className="mt-4 text-sm text-danger-600">주문을 불러오지 못했습니다.</p>
        ) : (
          <>
            {/* 주문자 / 수령 정보 */}
            <dl className="mt-3 flex flex-col gap-1 rounded-md border border-ink-200 bg-ink-50 p-3 text-sm">
              <DetailRow label="유형" value={ORDER_TYPE_LABEL_KO[order.type]} />
              <DetailRow label="주문자" value={order.customer.name ?? "회원"} />
              <DetailRow
                label="연락처"
                value={order.customer.phone ? formatPhone(order.customer.phone) : "-"}
              />
              <DetailRow
                label="수령"
                value={formatDesiredPickup(order.desiredDate, order.desiredTime)}
              />
              {order.delivery ? (
                <>
                  <DetailRow
                    label="받는 분"
                    value={`${order.delivery.recipientName} (${formatPhone(order.delivery.recipientPhone)})`}
                  />
                  <DetailRow
                    label="배송지"
                    value={`${order.delivery.addressLine1}${order.delivery.addressLine2 ? ` ${order.delivery.addressLine2}` : ""}`}
                  />
                </>
              ) : null}
              {order.customerMemo ? <DetailRow label="요청사항" value={order.customerMemo} /> : null}
            </dl>

            {/* 주문 품목 + 금액 */}
            <ul className="mt-2 flex flex-col gap-1 rounded-md bg-ink-50 p-3 text-sm">
              {order.items.map((it) => (
                <li key={it.id} className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 flex-1 truncate">
                    {it.name}{" "}
                    <span className="text-ink-600">({formatCartLineLabel(units, it.unit, it.quantity)})</span>
                  </span>
                  <span className="shrink-0 tabular-nums">{it.lineTotalKrw.toLocaleString()}원</span>
                </li>
              ))}
              {order.appliedCoupons.length > 0 ? (
                <li className="flex items-baseline justify-between text-brand-700">
                  <span>쿠폰 {order.appliedCoupons.map((c) => c.name).join(", ")}</span>
                  <span className="tabular-nums">
                    {order.discountKrw > 0 ? `-${order.discountKrw.toLocaleString()}원` : "배달비 무료"}
                  </span>
                </li>
              ) : null}
              {order.complimentDiscountKrw > 0 ? (
                <li className="flex items-baseline justify-between text-brand-700">
                  <span>칭찬 감사 할인{order.complimentText ? ` (${order.complimentText})` : ""}</span>
                  <span className="tabular-nums">-{order.complimentDiscountKrw.toLocaleString()}원</span>
                </li>
              ) : null}
              {order.pointsUsedKrw > 0 ? (
                <li className="flex items-baseline justify-between text-brand-700">
                  <span>적립금 사용</span>
                  <span className="tabular-nums">-{order.pointsUsedKrw.toLocaleString()}원</span>
                </li>
              ) : null}
              <li className="mt-1 flex items-baseline justify-between border-t border-ink-200 pt-1 font-semibold">
                <span>합계 (현장 결제)</span>
                <span className="tabular-nums">{order.totalKrw.toLocaleString()}원</span>
              </li>
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

// 상세 모달 정보 한 줄.
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="w-16 shrink-0 text-ink-600">{label}</dt>
      <dd className="min-w-0 flex-1 text-ink-800">{value}</dd>
    </div>
  );
}
